/**
 * ============================================================================
 * [P0-03] Firebase Auth Hook
 *
 * Firebase 인증 상태 관리 React Hook
 *
 * 주요 기능:
 * - 인증 상태 실시간 감지 (onAuthStateChanged)
 * - Google 로그인/로그아웃
 * - 익명 로그인 (레거시 지원)
 * - iOS 환경 토큰 동기화
 *
 * Safety:
 * - onAuthStateChanged unsubscribe 필수 처리 (Memory Leak 방지)
 * - Firebase SDK 직접 사용 (AuthManager 클래스 미사용)
 * ============================================================================
 */

import { useState, useEffect, useCallback } from 'react'
import {
  onAuthStateChanged,
  signInWithPopup,
  signInAnonymously,
  signOut,
  GoogleAuthProvider,
  type User,
} from 'firebase/auth'
import { auth } from '@/services/firebase'

// ============================================================================
// 타입 정의
// ============================================================================
interface AuthState {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  error: Error | null
}

interface AuthActions {
  loginWithGoogle: () => Promise<LoginResult>
  loginAnonymously: () => Promise<LoginResult>
  logout: () => Promise<boolean>
  waitForToken: (maxAttempts?: number) => Promise<boolean>
}

interface LoginResult {
  success: boolean
  user?: User
  error?: Error
}

export type UseAuthReturn = AuthState & AuthActions

// ============================================================================
// 플랫폼 감지 유틸리티
// ============================================================================

/**
 * iOS/iPadOS 플랫폼 여부 확인
 */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

/**
 * 모바일 플랫폼 여부 확인
 */
function isMobile(): boolean {
  if (typeof navigator === 'undefined') return false
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

// ============================================================================
// [P0-03] useAuth Hook
// ============================================================================
export function useAuth(): UseAuthReturn {
  // ========================================================================
  // State 정의
  // ========================================================================
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  // ========================================================================
  // [Safety] onAuthStateChanged 리스너 설정 및 Cleanup
  // - useEffect cleanup 함수로 unsubscribe 처리 (Memory Leak 방지)
  // ========================================================================
  useEffect(() => {
    console.log('[useAuth] Auth state listener 설정 시작')

    // onAuthStateChanged 리스너 등록
    const unsubscribe = onAuthStateChanged(
      auth,
      (firebaseUser) => {
        if (firebaseUser) {
          console.log('[useAuth] 사용자 로그인:', firebaseUser.displayName || firebaseUser.uid)
          setUser(firebaseUser)
        } else {
          console.log('[useAuth] 사용자 로그아웃')
          setUser(null)
        }
        setIsLoading(false)
        setError(null)
      },
      (err) => {
        console.error('[useAuth] Auth state error:', err)
        setError(err)
        setIsLoading(false)
      }
    )

    // [Safety] Cleanup: 컴포넌트 언마운트 시 리스너 해제
    return () => {
      console.log('[useAuth] Auth state listener 해제 (cleanup)')
      unsubscribe()
    }
  }, []) // 빈 배열: 마운트 시 한 번만 실행

  // ========================================================================
  // [iOS Patch] ID 토큰 준비 상태 대기
  // - iOS 환경에서 인증 직후 Firestore 접근 시 권한 오류 방지
  // ========================================================================
  const waitForToken = useCallback(async (maxAttempts: number = 10): Promise<boolean> => {
    if (!auth.currentUser) return false

    console.log('[useAuth] [iOS Patch] ID 토큰 동기화 대기 시작...')

    for (let i = 0; i < maxAttempts; i++) {
      const currentUser = auth.currentUser
      if (currentUser) {
        try {
          // forceRefresh=true로 최신 토큰 강제 수신
          const token = await currentUser.getIdToken(true)
          if (token) {
            console.log('[useAuth] [iOS Patch] ID 토큰 준비 완료')
            return true
          }
        } catch (e) {
          console.warn(
            `[useAuth] [iOS Patch] 토큰 갱신 시도 중 (${i + 1}/${maxAttempts}):`,
            (e as Error).message
          )
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 500))
    }

    console.error('[useAuth] [iOS Patch] ID 토큰 준비 타임아웃')
    return false
  }, [])

  // ========================================================================
  // Google 로그인
  // ========================================================================
  const loginWithGoogle = useCallback(async (): Promise<LoginResult> => {
    setIsLoading(true)
    setError(null)

    try {
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)

      console.log('[useAuth] Google 로그인 성공:', result.user.email)

      // iOS/모바일 환경에서 토큰 동기화 대기
      if (isIOS() || isMobile()) {
        console.log('[useAuth] [iOS/Mobile Patch] 토큰 동기화 대기 중...')
        await waitForToken()
      }

      return { success: true, user: result.user }
    } catch (err) {
      const error = err as Error & { code?: string }
      console.error('[useAuth] Google 로그인 실패:', error)

      setError(error)

      // 사용자가 팝업을 닫은 경우는 에러로 처리하지 않음
      if (error.code === 'auth/popup-closed-by-user') {
        console.log('[useAuth] 로그인 취소됨 (팝업 닫힘)')
      }

      return { success: false, error }
    } finally {
      setIsLoading(false)
    }
  }, [waitForToken])

  // ========================================================================
  // 익명 로그인 (레거시 지원)
  // - 기존 사용자명 기반 로그인을 위한 익명 인증
  // ========================================================================
  const loginAnonymously = useCallback(async (): Promise<LoginResult> => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await signInAnonymously(auth)

      console.log('[useAuth] 익명 로그인 성공:', result.user.uid)

      // iOS/모바일 환경에서 토큰 동기화 대기
      if (isIOS() || isMobile()) {
        await waitForToken()
      }

      return { success: true, user: result.user }
    } catch (err) {
      const error = err as Error
      console.error('[useAuth] 익명 로그인 실패:', error)

      setError(error)
      return { success: false, error }
    } finally {
      setIsLoading(false)
    }
  }, [waitForToken])

  // ========================================================================
  // 로그아웃
  // ========================================================================
  const logout = useCallback(async (): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      await signOut(auth)
      console.log('[useAuth] 로그아웃 성공')
      return true
    } catch (err) {
      const error = err as Error
      console.error('[useAuth] 로그아웃 실패:', error)
      setError(error)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  // ========================================================================
  // Return 값
  // ========================================================================
  return {
    // State
    user,
    isLoading,
    isAuthenticated: !!user,
    error,

    // Actions
    loginWithGoogle,
    loginAnonymously,
    logout,
    waitForToken,
  }
}

// ============================================================================
// 기본 export
// ============================================================================
export default useAuth
